"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { institutionService } from "@/services/institution";

interface InstitutionButtonColorsContextType {
  addButtonColor: string;
  cancelButtonColor: string;
  setAddButtonColor: (color: string) => void;
  setCancelButtonColor: (color: string) => void;
  resetColors: () => void;
  isColorSet: boolean;
  isLoading: boolean;
}

export const InstitutionButtonColorsContext = createContext<
  InstitutionButtonColorsContextType | undefined
>(undefined);

const DEFAULT_ADD_COLOR = "#0070f3"; // default blue
const DEFAULT_CANCEL_COLOR = "#6b7280"; // gray-500

interface InstitutionButtonColorsProviderProps {
  children: ReactNode;
}

export function InstitutionButtonColorsProvider({
  children,
}: InstitutionButtonColorsProviderProps) {
  const [addButtonColor, setAddButtonColorState] =
    useState<string>(DEFAULT_ADD_COLOR);
  const [cancelButtonColor, setCancelButtonColorState] =
    useState<string>(DEFAULT_CANCEL_COLOR);
  const [isColorSet, setIsColorSet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load colors from backend and localStorage on mount
  useEffect(() => {
    const fetchColors = async () => {
      // Only fetch colors if user is authenticated
      if (typeof window !== "undefined") {
        const isAuthenticated = localStorage.getItem("isAuthenticated");
        const userRole = localStorage.getItem("userRole");

        // Don't fetch if not authenticated or not an institution
        if (isAuthenticated !== "true" || userRole !== "institution") {
          console.log("🏫 Skipping color fetch - user not authenticated as institution");
          return;
        }
      }

      try {
        setIsLoading(true);

        console.log("🏫 Fetching institution colors from backend...");

        // Try to fetch from backend first
        const response = await institutionService.getColors();

        console.log("🏫 Institution details response:", response);

        if (response.success === 1 && response.data) {
          const { primary_colour, secondary_colour } = response.data;

          console.log("🏫 Fetched colors:", { primary_colour, secondary_colour });

          if (primary_colour || secondary_colour) {
            if (primary_colour) {
              console.log("🏫 Setting primary color:", primary_colour);
              setAddButtonColorState(primary_colour);
            } else {
              console.log("🏫 Primary color is null, using default");
            }
            if (secondary_colour) {
              console.log("🏫 Setting secondary color:", secondary_colour);
              setCancelButtonColorState(secondary_colour);
            } else {
              console.log("🏫 Secondary color is null, using default");
            }
            setIsColorSet(true); // Lock if colors exist in DB

            // Sync to local storage for persistence just in case
            if (typeof window !== "undefined") {
              if (primary_colour) localStorage.setItem("institution-add-button-color", primary_colour);
              if (secondary_colour) localStorage.setItem("institution-cancel-button-color", secondary_colour);
            }
            return; // Backend source of truth, exit
          } else {
            console.log("🏫 Both colors are null");
          }
        } else {
          console.log("🏫 Backend response unsuccessful or no data:", response);
        }
      } catch (error) {
        console.error("❌ Failed to fetch institution colors:", error);
      } finally {
        setIsLoading(false);
      }

      // Fallback to localStorage if backend didn't have colors
      console.log("🏫 Falling back to localStorage");
      if (typeof window !== "undefined") {
        const savedAddColor = localStorage.getItem("institution-add-button-color");
        const savedCancelColor = localStorage.getItem(
          "institution-cancel-button-color",
        );

        console.log("🏫 localStorage colors:", { savedAddColor, savedCancelColor });

        if (savedAddColor) {
          setAddButtonColorState(savedAddColor);
        }
        if (savedCancelColor) {
          setCancelButtonColorState(savedCancelColor);
        }
      }
    };

    fetchColors();
  }, []);

  const setAddButtonColor = (color: string) => {
    if (isColorSet) return; // Prevent update if locked
    setAddButtonColorState(color);
    if (typeof window !== "undefined") {
      localStorage.setItem("institution-add-button-color", color);
    }
  };

  const setCancelButtonColor = (color: string) => {
    if (isColorSet) return; // Prevent update if locked
    setCancelButtonColorState(color);
    if (typeof window !== "undefined") {
      localStorage.setItem("institution-cancel-button-color", color);
    }
  };

  const resetColors = () => {
    if (isColorSet) return; // Prevent reset if locked
    setAddButtonColorState(DEFAULT_ADD_COLOR);
    setCancelButtonColorState(DEFAULT_CANCEL_COLOR);
    if (typeof window !== "undefined") {
      localStorage.removeItem("institution-add-button-color");
      localStorage.removeItem("institution-cancel-button-color");
    }
  };

  return (
    <InstitutionButtonColorsContext.Provider
      value={{
        addButtonColor,
        cancelButtonColor,
        setAddButtonColor,
        setCancelButtonColor,
        resetColors,
        isColorSet,
        isLoading,
      }}
    >
      {children}
    </InstitutionButtonColorsContext.Provider>
  );
}

export const useInstitutionButtonColors = () => {
  const context = useContext(InstitutionButtonColorsContext);
  if (!context) {
    // Return default values if used outside provider (e.g. in vendor view)
    return {
      addButtonColor: DEFAULT_ADD_COLOR,
      cancelButtonColor: DEFAULT_CANCEL_COLOR,
      setAddButtonColor: () => { },
      setCancelButtonColor: () => { },
      resetColors: () => { },
      isColorSet: false,
      isLoading: false,
    };
  }
  return context;
};
